import React, { useState } from 'react';
import { Star, Coffee, Send, ArrowLeft, HelpCircle, Maximize2, X } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';

interface RatingData {
  aroma: number;
  flavor: number;
  body: number;
  acidity: number;
  finish: number;
  overall: number;
  notes: string;
  name: string;
  email: string;
  brewMethod: string;
  grindSize: string;
}

const RateYourRoast: React.FC = () => {
  const { roastId } = useParams<{ roastId: string }>();
  const navigate = useNavigate();
  const [ratings, setRatings] = useState<RatingData>({
    aroma: 0,
    flavor: 0,
    body: 0,
    acidity: 0,
    finish: 0,
    overall: 0,
    notes: '',
    name: '',
    email: '',
    brewMethod: '',
    grindSize: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<'brazil' | 'guatemala' | null>(null);

  const nunnepogFogConfig = {
    name: 'Nunnepog Fog Espresso Blend',
    emoji: '🌫️',
    batch: 'Batch 1',
    productImage: 'https://i.ibb.co/xtC0KDLt/Nunnepog-Fog-4-x-6-in-1.png',
    story: {
      intro: 'Designed for espresso lovers, perfect for any brew. The world can wait. Pour the quiet.',
      paragraphs: [
        'Nunnepog Fog was crafted for espresso drinkers who crave depth and balance, yet it shines in any form — pour-over, drip, or French press. It\'s made for those mornings when the harbor vanishes, the air thickens, and the world feels half-asleep in its own dream.',
        'On Martha\'s Vineyard, Nunnepog comes from the Wampanoag word for "a pond" — or more softly, "when there is water there." A phrase that fits New England perfectly: beauty that drifts in and out, like tides, like fog, like old friends returning.',
        'This roast began with Rick and Jane Conti, early Bond believers who reminded us that coffee isn\'t about caffeine — it\'s about connection. The best moments happen in the pause — between one thing and the next.',
        'Nunnepog Fog is for mornings like that — when Marblehead and the Vineyard breathe the same mist, when the world softens at the edges and you feel smaller, gentler, more yourself. Whether pulled as a shot or brewed slow, it\'s a Bon Iver kind of moment — not magnificent, but everything.',
        'So pour a cup. Let it cool. Watch the fog hold the harbor. The world can wait. Pour the quiet.'
      ]
    },
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&q=80&w=800',
    imageCaption: 'Morning fog over Marblehead Harbor',
    tastingNotes: [
      { emoji: '🌫️', name: 'Misty' },
      { emoji: '🍯', name: 'Honey' },
      { emoji: '🌰', name: 'Hazelnut' },
      { emoji: '🕊️', name: 'Gentle' }
    ],
    roastProfile: {
      level: 'Espresso Roast',
      body: 'Soft',
      acidity: 'Gentle',
      bestFor: 'Espresso, Pour-over, Drip, French Press'
    },
    origins: [
      {
        name: 'Guatemala Antigua - The Gentle Foundation',
        percentage: '50%',
        description: 'From Finca Medina in the volcanic soils of Antigua comes the heart of Nunnepog Fog. Like the quiet moments Rick and Jane cherished, this washed Guatemalan coffee provides the gentle, balanced foundation that makes every sip feel like a warm embrace on a foggy morning. Clean and transparent with a classic Antigua profile that gains roundness as it cools.',
        tastingNotes: 'Citrusy start (orange, mandarin, lemon), floral touch, cocoa, caramel, nutty base, hint of red fruit (cherry, strawberry), smooth mouthfeel, creamy texture',
        profileUrl: null // Add if you have roast profiles
      },
      {
        name: 'El Salvador Atiquizaya Tabi - The Quiet Complexity',
        percentage: '50%',
        description: 'From Finca Miravalles in Apaneca, Ahuachapán, near the Santa Ana volcano, comes the subtle complexity that makes Nunnepog Fog special. This disease-resistant Tabi varietal (a cross of Bourbon, Typica, and Timor Hybrid) adds the gentle brightness and sweet balance that emerges slowly, like fog revealing hidden beauty to those who pause to notice.',
        tastingNotes: 'Raw sugar sweetness, caramel, cocoa powder, cinnamon-sugar toast, roasted barley, tea-like snap, well-integrated acidity, bittersweet chocolate finish',
        profileUrl: null // Add if you have roast profiles
      }
    ],
    craftedPurpose: 'Like the fog that connects Marblehead to the Vineyard, this 50/50 blend brings together Guatemalan clarity and Salvadoran sweetness — two gentle origins that complement rather than compete. The Antigua provides the clean, citrusy foundation while the Tabi adds crowd-pleasing balance and integrated sweetness. Each bean is roasted with the same care Rick and Jane brought to every quiet morning — creating a coffee that invites you to slow down, breathe deep, and find beauty in the pause between moments.'
  };

  // Roast configurations
  const roastConfigs = {
    'barnegat-breakfast': {
      name: 'Barnegat Breakfast',
      emoji: '🌅',
      batch: 'Batch 1',
      productImage: 'https://i.ibb.co/Kj3zbtXd/Barnegat-Breakfast-4-x-6-in.png',
      story: {
        intro: 'Our signature morning blend, named after the place where Marblehead\'s story began – and where ours began too.',
        paragraphs: [
          'Centuries ago, brave souls found sanctuary in Barnegat\'s protected cove, sheltered by Crowninshield\'s and Gerry\'s islands. Here, in Little Harbor, they built the first homes, the first meeting house, and formed the bonds that would become Marblehead. Small fishing boats called shallops ventured out each morning, while sailing ships carried dreams across the Atlantic.',
          'Just as those early settlers found their perfect harbor, Chris and Adam found their perfect beginning as neighbors in this same historic place. Like the shallops that started small but carried big dreams, Bond Coffee began with two friends, a shared passion, and the same spirit of adventure that has always defined Marblehead.',
          'This blend is our tribute to all beginnings – to the courage it takes to start something new, to the bonds that sustain us, and to the perfect morning that awaits when you\'ve found your harbor, your community, your home.'
        ]
      },
      image: 'https://i.ibb.co/d0z1Q84M/marblehead-harbor-historic.jpg',
      imageCaption: 'You can almost see Bond HQ across the Harbor!',
      tastingNotes: [
        { emoji: '🍫', name: 'Chocolate' },
        { emoji: '🥜', name: 'Nutty' },
        { emoji: '🍯', name: 'Honey' },
        { emoji: '🍊', name: 'Citrus' }
      ],
      roastProfile: {
        level: 'Medium',
        body: 'Medium-Full',
        acidity: 'Balanced',
        bestFor: 'Drip, Pour-over'
      },
      origins: [
        {
          name: 'Brazil - The Foundation',
          percentage: '60%',
          description: 'From Luis Otavio\'s farm in Canastra comes the heart of our blend – rich, dependable, and deeply satisfying. Like the bedrock beneath Marblehead\'s harbor, this Brazilian coffee provides the chocolatey, nutty foundation that makes every morning feel like home.',
          tastingNotes: 'Bittersweet chocolate, candied peanuts, caramel, roasted almonds, high cocoa solids chocolate',
          profileUrl: 'https://roast.world/profiles/VTJGc2RHVmtYMThXSWIrVGpac0VRVWVLYStpeWp1TkFvcEVPZG93R0ptMkFqeWNCMDRVbnVmMUY5ZUppOFpobg?size=default'
        },
        {
          name: 'Guatemala - The Brightness',
          percentage: '40%',
          description: 'From the volcanic soils of Antigua\'s Pulcal farm comes the spark that brings our blend to life. Like the morning sun breaking over Marblehead Harbor, this Guatemalan coffee adds the brightness, sweetness, and complexity that turns a good cup into something extraordinary.',
          tastingNotes: 'Brown sugar sweetness, apple-like acidity, dark chocolate, almond, raisin, fig',
          profileUrl: 'https://roast.world/profiles/VTJGc2RHVmtYMStBcW4yNktHcHg4SmhyTVgxL0s2RldhRllsZmk3M0Zpb2V6dm9PbXYwaFhkbHpNcGJaYWJpbQ?size=default'
        }
      ],
      craftedPurpose: 'Like the friendship that started Bond Coffee, this blend brings together two distinct personalities that are better together. Each origin is roasted with care to its perfect profile, then united to create your ideal morning companion – consistent, comforting, and always there when you need it most.'
    },
    'soleless-shakedown': {
      name: 'Soleless Shakedown',
      emoji: '⚡',
      batch: 'Batch 1',
      productImage: 'https://i.ibb.co/yB83W3DB/Soleless-Shakedown-Final.png',
      story: {
        intro: 'Born from one of the most iconic Hocus Pocus scenes, Soleless Shakedown pays tribute to the moment Max Dennison got his sneakers stolen on the slopes of Marblehead\'s Old Burial Hill.',
        paragraphs: [
          'In 1993, Hocus Pocus stamped Marblehead\'s Old Burial Hill into pop culture forever. It\'s where Max Dennison ran into two bullies—Jay in his flannel and Ice with his shaved head—and walked away shoeless, leaving his fresh Nikes behind among the Puritan stones and harbor views.',
          'Three decades later, Bond Coffee is roasting a tribute that turns that shakedown into something better. Soleless Shakedown is bold, smooth, and just a little mischievous—crafted at the foot of Old Burial Hill where Marbleheaders, movie fans, and neighbors still gather.',
          'It\'s a reminder that even if you lose your soles, you can still keep your soul—and that love, kindness, and a good cup of coffee are always stronger than fear or hate.'
        ]
      },
      image: 'https://i.ibb.co/39hRVSHh/soleless-shakedown-image.jpg',
      imageCaption: 'Old Burial Hill - where history and Halloween collide',
      tastingNotes: [
        { emoji: '🍫', name: 'Cocoa' },
        { emoji: '🥜', name: 'Roasted Nuts' },
        { emoji: '💨', name: 'Whisper of Smoke' },
        { emoji: '👟', name: 'Hints of Stolen Sneakers*' }
      ],
      roastProfile: {
        level: 'Medium-Dark',
        body: 'Full',
        acidity: 'Balanced',
        bestFor: 'Drip, French Press, Pour-over'
      },
      origins: [
        {
          name: 'Brazil - The Bold Foundation',
          percentage: '60%',
          description: 'From the highlands of Minas Gerais comes the backbone of Soleless Shakedown. Like the ancient stones of Old Burial Hill, this Brazilian coffee provides the rich, chocolatey foundation that anchors every cup with strength and character.',
          tastingNotes: 'Dark chocolate, roasted almonds, caramel, cocoa powder, brown sugar',
          profileUrl: 'https://roast.world/profiles/VTJGc2RHVmtYMS9kYlphL1Y3QkVsdk8wRDV4ZHJWUWs0VE51TWlIN2dVdEdMSVhJc0xNTGhpMGdpSm5neVhtNg?size=default',
          roastLevel: 'Medium'
        },
        {
          name: 'El Salvador - The Smooth Balance',
          percentage: '30%',
          description: 'From Finca Miravalles in Apaneca, Ahuachapán, comes the crowd-pleasing balance that makes Soleless Shakedown so approachable. Grown at 1500 meters near the Santa Ana volcano, this Tabí varietal brings well-integrated acidity and sweet complexity that perfectly complements the bold Brazilian foundation.',
          tastingNotes: 'Raw sugar sweetness, caramel, cocoa powder, cinnamon-sugar toast, roasted barley, balanced acidity',
          profileUrl: 'https://roast.world/profiles/VTJGc2RHVmtYMThRSitxL1VOZEU4bnFrRWJCTktNZ0k4ZDFLU0xsWmQrd3p2aHFPUERncFJiUjN1WUZWUEg5bA?size=large',
          roastLevel: 'Medium'
        },
        {
          name: 'Ethiopian - The Bright Mystery',
          percentage: '10%',
          description: 'From the birthplace of coffee comes the bright, mysterious element that makes Soleless Shakedown truly special. Like the unexpected magic of Halloween night in Marblehead, this Ethiopian coffee adds floral brightness and complexity that lingers long after the last sip.',
          tastingNotes: 'Impressive sweetness, cream soda, nectarine and lemon accents, clove in aroma, peach-like acidity',
          profileUrl: 'https://roast.world/profiles/VTJGc2RHVmtYMThTRjNlTFVFU2M0TWFQRGUwV0FlMHNnSUsvM0Z0dWt4YXQzbGNSdUVtcnlSc0JWU0Z6NzRLdw?size=large',
          roastLevel: 'Light'
        }
      ],
      craftedPurpose: 'Like the legendary scene that inspired it, Soleless Shakedown brings together three distinct personalities: Brazilian boldness, Salvadoran smoothness, and Ethiopian brightness. Each origin is roasted to perfection, then blended to create a coffee that\'s memorable, complex, and undeniably Marblehead — perfect for those who know that losing your shoes doesn\'t mean losing your swagger.'
    },
    'nunnepog-fog': nunnepogFogConfig,
    'wake-with-drake': {
      name: 'Wake with Drake',
      emoji: '🏈',
      batch: 'New Release',
      productImage: '/chatgpt_image_jan_12,_2026_at_02_10_15_pm.png',
      story: {
        intro: 'For football mornings, family traditions, and passing down what matters. Because football mornings just hit different.',
        paragraphs: [
          'There\'s nothing better than watching your kids discover something you\'ve loved forever — and somehow love it just as loudly.',
          'For us, that thing is football. What started as Adam and me watching games with coffee in hand has turned into kids asking questions, arguing calls, and running their own fantasy football league. Somewhere along the way, it stopped being just about the game and became about being together.',
          'Wake with Drake is our nod to football mornings, late games, early kickoffs, and the small rituals that stick around. It\'s for making coffee before the house wakes up (or explodes), for sitting on the couch together, and for passing down more than just favorite teams.',
          'The roast itself is bold, smooth, and dependable — the kind of cup you want before kickoff, school drop-off, or explaining why a perfectly good fantasy lineup somehow still lost.',
          'Drink it during the game. Drink it while talking trash with your kids. Drink it because some traditions are worth waking up for.',
          'Wake with Drake. Because football mornings just hit different.'
        ]
      },
      image: 'https://images.unsplash.com/photo-1461023058943-07fcbe16d735?w=1920&q=80',
      imageCaption: 'Sunday morning football tradition',
      tastingNotes: [
        { emoji: '🏈', name: 'Bold' },
        { emoji: '☕', name: 'Smooth' },
        { emoji: '🛡️', name: 'Dependable' },
        { emoji: '🎯', name: 'Game Day' }
      ],
      roastProfile: {
        level: 'Medium',
        body: 'Bold',
        acidity: 'Balanced',
        bestFor: 'Drip, Pour Over, French Press'
      },
      origins: [],
      craftedPurpose: 'Wake with Drake delivers a bold yet smooth experience with dependable flavor from start to finish. The perfect companion for early mornings, game days, and everything in between — because football mornings just hit different.'
    }
  };

  const currentRoast = roastId ? roastConfigs[roastId as keyof typeof roastConfigs] : null;

  // Redirect if invalid roast ID
  React.useEffect(() => {
    if (!roastId || !currentRoast) {
      navigate('/rateyouroast');
    }
  }, [roastId, currentRoast, navigate]);

  if (!currentRoast) {
    return null; // Will redirect
  }

  const questions = [
    {
      key: 'aroma' as keyof RatingData,
      title: 'Aroma',
      description: 'How pleasant and complex was the aroma?',
      tooltip: 'The dry fragrance of the grounds and wet aroma when brewing. Look for complexity, intensity, and pleasantness of the coffee\'s smell.'
    },
    {
      key: 'flavor' as keyof RatingData,
      title: 'Flavor',
      description: 'How well-balanced and enjoyable were the flavors?',
      tooltip: 'The overall taste experience - sweetness, fruitiness, nuttiness, chocolate notes. How well do the flavors balance and complement each other?'
    },
    {
      key: 'body' as keyof RatingData,
      title: 'Body',
      description: 'How did the body/mouthfeel work for this coffee?',
      tooltip: 'The weight and texture of the coffee on your palate. Light feels thin like tea, medium has substance, full feels rich and heavy.'
    },
    {
      key: 'acidity' as keyof RatingData,
      title: 'Acidity',
      description: 'How well-balanced was the acidity/brightness?',
      tooltip: 'The bright, tangy quality that makes coffee lively. Good acidity is crisp and pleasant, like apple or citrus notes, not sour or harsh.'
    },
    {
      key: 'finish' as keyof RatingData,
      title: 'Finish',
      description: 'How pleasant and lasting was the aftertaste?',
      tooltip: 'The flavors that remain after swallowing. A good finish is pleasant, long-lasting, and clean - not bitter or unpleasant.'
    }
  ];

  const handleStarClick = (category: keyof RatingData, rating: number) => {
    setRatings(prev => ({ ...prev, [category]: rating }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    console.log('Form submission started', { ratings });

    // Validate required fields
    if (ratings.overall === 0) {
      setError('Please provide an overall rating to submit');
      setIsSubmitting(false);
      return;
    }

    try {
      console.log('Attempting to insert rating into database');

      const { error: dbError } = await supabase
        .from('coffee_ratings')
        .insert([{
          blend_name: currentRoast.name,
          aroma_rating: ratings.aroma || 3,
          flavor_rating: ratings.flavor || 3,
          body_rating: ratings.body || 3,
          acidity_rating: ratings.acidity || 3,
          finish_rating: ratings.finish || 3,
          overall_rating: ratings.overall,
          notes: ratings.notes || null,
          reviewer_name: ratings.name || null,
          reviewer_email: ratings.email || null,
          brew_method: ratings.brewMethod || null,
          grind_size: ratings.grindSize || null
        }]);

      if (dbError) {
        console.error('Database error:', dbError);
        setError(`Database error: ${dbError.message || 'Unknown error'}`);
        throw dbError;
      }

      console.log('Rating submitted successfully');
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting rating:', err);
      setError(`Failed to submit rating: ${err instanceof Error ? err.message : 'Unknown error'}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const RoastProfileModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    title: string;
    description: string;
    iframeSrc: string;
    origin: string;
    blendPercent: string;
    profile: string;
  }> = ({ isOpen, onClose, title, description, iframeSrc, origin, blendPercent, profile }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-[#132947] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-[#132947] p-4 border-b border-white/10 flex justify-between items-center">
            <h3 className="text-xl font-bold text-white">{title}</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
          
          <div className="p-6 space-y-6">
            <p className="text-white/80">{description}</p>
           <h3 className="text-white font-semibold mb-6 text-center">Three Origins, One Story</h3>
            <div className="bg-white/10 p-4 rounded-lg">
              <div className="aspect-video w-full overflow-hidden">
                <iframe
                  src={iframeSrc}
                  title={title}
                  frameBorder="0"
                  sandbox="allow-scripts allow-same-origin"
                  allowFullScreen
                  className="w-full h-full rounded-lg"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="bg-white/5 p-3 rounded-lg text-center">
                <div className="font-semibold text-[#fda36b]">Origin</div>
                <div className="text-white/90">{origin}</div>
              </div>
              <div className="bg-white/5 p-3 rounded-lg text-center">
                <div className="font-semibold text-[#fda36b]">Blend %</div>
                <div className="text-white/90">{blendPercent}</div>
              </div>
              <div className="bg-white/5 p-3 rounded-lg text-center">
                <div className="font-semibold text-[#fda36b]">Profile</div>
                <div className="text-white/90">{profile}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const StarRating: React.FC<{ 
    rating: number; 
    onRate: (rating: number) => void;
    size?: 'small' | 'large';
  }> = ({ rating, onRate, size = 'small' }) => {
    const starSize = size === 'large' ? 'w-8 h-8' : 'w-6 h-6';
    
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onRate(star)}
            className={`${starSize} transition-all duration-200 hover:scale-110`}
          >
            <Star
              className={`w-full h-full ${
                star <= rating
                  ? 'text-[#fda36b] fill-[#fda36b]'
                  : 'text-white/30 hover:text-[#fda36b]/50'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#132947]">
        <Header />
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-2xl mx-auto text-center space-y-8">
            <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto animate-pulse">
              <Coffee className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">
              Thanks for the Feedback! ☕
            </h1>
            <p className="text-xl text-white/80">
              Your rating helps us perfect our roasting process. We read every single review 
              and use your feedback to make each batch even better.
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-[#fda36b] text-[#132947] px-8 py-4 rounded-lg font-semibold hover:bg-[#fda36b]/90 transition-all duration-300"
            >
              Back to Bond Coffee
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#132947]">
      <Header />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/rateyouroast')}
            className="flex items-center gap-2 text-white/60 hover:text-[#fda36b] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Roast Selection
          </button>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="text-6xl mb-6">☕</div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Rate Your Roast
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Help us perfect our craft. Your feedback shapes how we roast future batches.
            </p>
          </div>

          {/* Blend Information */}
          <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-8 rounded-2xl border border-[#fda36b]/20 mb-12">
            <div className="space-y-8">
              {/* Header Section */}
              <div className="text-center">
                <h2 className="text-3xl font-bold text-[#fda36b] mb-4">
                  {currentRoast.name}
                </h2>
                <div className="mb-4 space-y-2">
                  <div className="inline-block bg-[#fda36b]/20 px-3 py-1 rounded-full">
                    <span className="text-[#fda36b] font-semibold text-sm">{currentRoast.batch}</span>
                  </div>
                </div>
              </div>
              
              {/* Story and Details Grid */}
              <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
                {/* Story Section - Takes up 2 columns */}
                <div className="lg:col-span-2 space-y-4 lg:space-y-6">
                  <div className="space-y-4 lg:space-y-6 mb-4 lg:mb-6">
                  <p className="text-sm lg:text-base text-white/90 leading-relaxed">
                    {currentRoast.story.intro}
                  </p>
                  
                  {currentRoast.story.paragraphs.map((paragraph, index) => (
                    <p key={index} className="text-sm lg:text-base text-white/90 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                  </div>
                </div>
                
                {/* Quick Info Sidebar */}
                <div className="space-y-4 lg:space-y-6">
                  {/* Product Image */}
                  {currentRoast.productImage && (
                  <div className="bg-white/5 p-2 rounded-lg border border-white/10 max-w-[250px] lg:max-w-none mx-auto lg:mx-0">
                    <div className="aspect-[3/4] overflow-hidden rounded-lg flex items-center justify-center p-1">
                    <img 
                      src={currentRoast.productImage} 
                      alt={`${currentRoast.name} coffee bag`}
                      className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity duration-300"
                    />
                    </div>
                  </div>
                  )}
                  
                  <div>
                    <h3 className="text-white font-semibold mb-2 lg:mb-3 text-sm lg:text-base">Tasting Notes</h3>
                    <div className="grid grid-cols-2 gap-2 lg:gap-3">
                      {currentRoast.tastingNotes.map((note, index) => (
                        <div key={index} className="bg-white/10 p-2 lg:p-3 rounded-lg text-center">
                          <div className="text-lg lg:text-2xl mb-1">{note.emoji}</div>
                          <div className="text-white/90 text-xs lg:text-sm">{note.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-2 lg:mb-3 text-sm lg:text-base">Roast Profile</h3>
                    <div className="space-y-1 lg:space-y-2 text-xs lg:text-sm">
                      <div className="flex justify-between">
                        <span className="text-white/70">Roast Level:</span>
                        <span className="text-white">{currentRoast.roastProfile.level}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">Body:</span>
                        <span className="text-white">{currentRoast.roastProfile.body}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">Acidity:</span>
                        <span className="text-white">{currentRoast.roastProfile.acidity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">Best For:</span>
                        <span className="text-white">{currentRoast.roastProfile.bestFor}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Origins Section */}
              <div>
           <h3 className="text-white font-semibold mb-6 text-center">Three Origins, One Story</h3>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                      {currentRoast.origins.map((origin, index) => (
                        <div key={index} className="bg-white/5 p-4 rounded-lg border border-white/10">
                          <h4 className="text-[#fda36b] font-semibold mb-2">{origin.name} ({origin.percentage})</h4>
                          <p className="text-white/80 text-sm mb-3">
                            {origin.description}
                          </p>
                          <div className="text-white/70 text-xs">
                            <strong>Tasting Notes:</strong> {origin.tastingNotes}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              
              {/* Crafted with Purpose */}
              <div className="text-center">
                  <div>
                    <h3 className="text-white font-semibold mb-2">Crafted with Purpose</h3>
                    <p className="text-white/80 text-sm">
                      {currentRoast.craftedPurpose}
                    </p>
                  </div>
              </div>
            </div>
          </div>

          {/* Roast Profiles Section */}
          {currentRoast.origins.some(origin => origin.profileUrl) && (
            <div className="bg-gradient-to-br from-white/10 to-white/5 p-8 rounded-2xl border border-white/20 mb-12">
            <h2 className="text-3xl font-bold text-[#fda36b] mb-6 text-center">
              Roast Profiles
            </h2>
            <p className="text-white/80 text-center mb-8 max-w-2xl mx-auto">
              Take a deep dive into the science behind {currentRoast.name}. These roast profiles show 
              the exact temperature curves and timing we use to bring out the best in each origin.
            </p>
            
            <div className="space-y-8">
              {currentRoast.origins.filter(origin => origin.profileUrl).map((origin, index) => (
                <div key={index} className="bg-white/5 p-6 rounded-xl border border-white/10">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-2xl font-bold text-white">
                      {origin.name} - {origin.percentage} of the blend
                    </h3>
                    <button
                      onClick={() => setActiveModal(index === 0 ? 'brazil' : 'guatemala')}
                      className="md:hidden bg-[#fda36b] text-[#132947] p-2 rounded-lg hover:bg-[#fda36b]/90 transition-colors"
                      title="View full roast profile"
                    >
                      <Maximize2 className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-white/80">
                    {origin.description}
                  </p>
                </div>
                
                <div className="bg-white/10 p-4 rounded-lg hidden md:block">
                  <div className="aspect-video w-full max-w-4xl mx-auto overflow-hidden">
                    <iframe
                      src={origin.profileUrl}
                      title={`${origin.name} roast profile`}
                      frameBorder="0"
                      sandbox="allow-scripts allow-same-origin"
                      allowFullScreen
                      className="w-full h-full rounded-lg scale-75 sm:scale-100 origin-top-left transform"
                    />
                  </div>
                </div>
                
                <div className="md:hidden bg-[#fda36b]/10 p-4 rounded-lg border border-[#fda36b]/20 text-center">
                  <p className="text-white/90 mb-3">Tap to view the detailed roast profile</p>
                  <button
                    onClick={() => setActiveModal(index === 0 ? 'brazil' : 'guatemala')}
                    className="bg-[#fda36b] text-[#132947] px-4 py-2 rounded-lg font-semibold hover:bg-[#fda36b]/90 transition-colors flex items-center gap-2 mx-auto"
                  >
                    <Maximize2 className="w-4 h-4" />
                    View {origin.name.split(' - ')[0]} Profile
                  </button>
                </div>
                
                <div className="mt-4 grid md:grid-cols-3 gap-4 text-sm">
                  <div className="bg-white/5 p-3 rounded-lg text-center">
                    <div className="font-semibold text-[#fda36b]">Origin</div>
                    <div className="text-white/90">{origin.name.split(' - ')[0]}</div>
                  </div>
                  <div className="bg-white/5 p-3 rounded-lg text-center">
                    <div className="font-semibold text-[#fda36b]">Blend %</div>
                    <div className="text-white/90">{origin.percentage}</div>
                  </div>
                  <div className="bg-white/5 p-3 rounded-lg text-center">
                    <div className="font-semibold text-[#fda36b]">Profile</div>
                    <div className="text-white/90">{origin.roastLevel || currentRoast.roastProfile.level} Roast</div>
                  </div>
                </div>
              </div>
              ))}
            </div>

            {/* Blend Summary */}
            <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-6 rounded-xl border border-[#fda36b]/20 mt-6">
              <h3 className="text-xl font-bold text-[#fda36b] mb-4 text-center">
                The Perfect Balance
              </h3>
              <p className="text-white/90 text-center leading-relaxed">
                {currentRoast.craftedPurpose}
              </p>
            </div>
          </div>
          )}

          {/* Rating Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Individual Ratings */}
            <div className="bg-white/5 p-8 rounded-2xl border border-white/10">
              <h3 className="text-2xl font-bold text-white mb-8 text-center">
                Rate Each Aspect
              </h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                {questions.map((question) => (
                  <div key={question.key} className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-lg font-semibold text-white">
                          {question.title}
                        </h4>
                        <div className="relative">
                          <button
                            type="button"
                            onMouseEnter={() => setShowTooltip(question.key)}
                            onMouseLeave={() => setShowTooltip(null)}
                            className="text-[#fda36b]/60 hover:text-[#fda36b] transition-colors p-1 rounded-full hover:bg-[#fda36b]/10"
                          >
                            <HelpCircle className="w-4 h-4" />
                          </button>
                          {showTooltip === question.key && (
                            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 max-w-[90vw] bg-[#0f1f35] text-white text-sm p-4 rounded-lg border-2 border-[#fda36b]/40 shadow-2xl z-50 backdrop-blur-sm">
                              <div className="text-[#fda36b] font-semibold mb-1">{question.title}</div>
                              <div className="text-white/90 leading-relaxed">{question.tooltip}</div>
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-white/70 text-sm mb-3">
                        {question.description}
                      </p>
                    </div>
                    <StarRating
                      rating={ratings[question.key] as number}
                      onRate={(rating) => handleStarClick(question.key, rating)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Overall Rating */}
            <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-8 rounded-2xl border border-[#fda36b]/20">
              <div className="text-center space-y-6">
                <h3 className="text-2xl font-bold text-white">
                  Overall Rating
                </h3>
                <p className="text-white/80">
                  How would you rate Barnegat Breakfast overall?
                </p>
                <StarRating
                  rating={ratings.overall}
                  onRate={(rating) => handleStarClick('overall', rating)}
                  size="large"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white/5 p-8 rounded-2xl border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">
                Additional Notes
              </h3>
              <textarea
                value={ratings.notes}
                onChange={(e) => setRatings(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Tell us more about your experience... What did you love? What could be better? Any other details about your brewing process?"
                className="w-full px-4 py-3 bg-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#fda36b] text-white placeholder-white/50 resize-none"
                rows={4}
              />
            </div>

            {/* Brewing Details */}
            <div className="bg-white/5 p-8 rounded-2xl border border-white/10">
              <h3 className="text-xl font-bold text-white mb-6">
                How Did You Brew It?
              </h3>
              <p className="text-white/70 text-sm mb-6">
                Help us understand how brewing method affects the taste experience.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Brewing Method */}
                <div>
                  <label className="block text-white font-semibold mb-2">Brewing Method</label>
                  <select
                    value={ratings.brewMethod}
                    onChange={(e) => setRatings(prev => ({ ...prev, brewMethod: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#fda36b] text-white"
                  >
                    <option value="">Select brewing method</option>
                    <option value="drip">Drip Coffee Maker</option>
                    <option value="pour-over">Pour Over (V60, Chemex, etc.)</option>
                    <option value="french-press">French Press</option>
                    <option value="aeropress">AeroPress</option>
                    <option value="espresso">Espresso Machine</option>
                    <option value="moka-pot">Moka Pot</option>
                    <option value="cold-brew">Cold Brew</option>
                    <option value="turkish">Turkish Coffee</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Grind Size */}
                <div>
                  <label className="block text-white font-semibold mb-2">Grind Size</label>
                  <select
                    value={ratings.grindSize}
                    onChange={(e) => setRatings(prev => ({ ...prev, grindSize: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#fda36b] text-white"
                  >
                    <option value="">Select grind size</option>
                    <option value="extra-coarse">Extra Coarse (Cold Brew)</option>
                    <option value="coarse">Coarse (French Press)</option>
                    <option value="medium-coarse">Medium-Coarse (Chemex)</option>
                    <option value="medium">Medium (Drip Coffee)</option>
                    <option value="medium-fine">Medium-Fine (Pour Over)</option>
                    <option value="fine">Fine (Espresso)</option>
                    <option value="extra-fine">Extra Fine (Turkish)</option>
                    <option value="pre-ground">Pre-Ground</option>
                    <option value="not-sure">Not Sure</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Optional Contact Info */}
            <div className="bg-white/5 p-8 rounded-2xl border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">
                Contact Info <span className="text-white/60 text-sm font-normal">(Optional)</span>
              </h3>
              <p className="text-white/70 text-sm mb-6">
                Leave your info if you'd like us to follow up, or stay anonymous – totally up to you!
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-semibold mb-2">Name</label>
                  <input
                    type="text"
                    value={ratings.name}
                    onChange={(e) => setRatings(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your name"
                    className="w-full px-4 py-3 bg-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#fda36b] text-white placeholder-white/50"
                  />
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Email</label>
                  <input
                    type="email"
                    value={ratings.email}
                    onChange={(e) => setRatings(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 bg-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#fda36b] text-white placeholder-white/50"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 text-red-400 p-4 rounded-lg border border-red-500/20">
                <p>{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#fda36b] text-[#132947] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#fda36b]/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mx-auto"
              >
                {isSubmitting ? (
                  'Submitting...'
                ) : (
                  <>
                    Submit Rating
                    <Send className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Roast Profile Modals */}
      <RoastProfileModal
        isOpen={activeModal === 'brazil'}
        onClose={() => setActiveModal(null)}
        title="Brazil Roast Profile - 60% of the blend"
        description="The foundation of our Barnegat Breakfast blend. This Brazilian coffee provides the chocolatey, nutty backbone that makes this blend so approachable and satisfying."
        iframeSrc="https://roast.world/profiles/VTJGc2RHVmtYMThXSWIrVGpac0VRVWVLYStpeWp1TkFvcEVPZG93R0ptMkFqeWNCMDRVbnVmMUY5ZUppOFpobg?size=default"
        origin="Brazil"
        blendPercent="60%"
        profile="Medium Roast"
      />
      
      <RoastProfileModal
        isOpen={activeModal === 'guatemala'}
        onClose={() => setActiveModal(null)}
        title="Guatemala Roast Profile - 40% of the blend"
        description="The bright, complex component that elevates Barnegat Breakfast. This Guatemalan coffee adds the citrusy brightness and honey sweetness that balances the Brazilian foundation."
        iframeSrc="https://roast.world/profiles/VTJGc2RHVmtYMStBcW4yNktHcHg4SmhyTVgxL0s2RldhRllsZmk3M0Zpb2V6dm9PbXYwaFhkbHpNcGJaYWJpbQ?size=default"
        origin="Guatemala"
        blendPercent="40%"
        profile="Medium Roast"
      />

      <Footer />
    </div>
  );
};

export default RateYourRoast;